import { useEffect } from "react";

import { useNavigate } from "react-router-dom";
import styled, { css, useTheme } from "styled-components";

import { ThemeMode } from "@sso/constant";
import { useMediaQuery } from "@sso/hooks/useMediaQuery";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { selectDrawerState, setDrawerItem, setDrawerOpen } from "@sso/store/slices/drawer";
import { DrawerItemType } from "@sso/types";
import { RowContainer, RowContainerProps } from "@sso/ui/FlexBox";
import { Typography } from "@sso/ui/Typography";

interface StyledNavItemProps extends RowContainerProps {
  selected: boolean;
}
const StyledNavItem = styled(RowContainer)<StyledNavItemProps>`
  justify-content: flex-start;
  padding: 0.75rem;
  margin: 0.1rem 0;
  width: 100%;
  cursor: pointer;
  border-radius: 1rem;
  color: ${p => (p.theme.mode === ThemeMode.LIGHT ? p.theme.colors.grey[800] : p.theme.colors.grey[200])};
  &:hover {
    background: ${p => (p.theme.mode === ThemeMode.LIGHT ? p.theme.colors.grey[200] : p.theme.colors.grey[700])};
  }
  ${p =>
    p.selected &&
    css`
      background: ${p.theme.mode === ThemeMode.LIGHT ? p.theme.colors.grey[300] : p.theme.colors.grey[700]};
      color: ${p.theme.colors.primary.main};
    `}
`;

interface NavItemProps {
  item: DrawerItemType;
}
export const NavItem = ({ item }: NavItemProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const matchesMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const user = useAppSelector(selectCurrentUser);
  const { selectedItem } = useAppSelector(selectDrawerState);

  const userHasAccess = item.access?.length
    ? item.access.some(role => role === user?.role.roleId.toLocaleLowerCase())
    : true;

  const handleNavClick = () => {
    item.url && navigate(item.url);
    dispatch(setDrawerItem(item.id));
    if (matchesMd) {
      dispatch(setDrawerOpen(false));
    }
  };

  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split("/")
      .findIndex(id => id === item.id);
    if (currentIndex > -1) {
      dispatch(setDrawerItem(item.id));
    }
  }, [dispatch, item.id]);

  if (!userHasAccess) {
    return null;
  }
  return (
    <StyledNavItem onClick={handleNavClick} selected={selectedItem === item.id}>
      {item.icon && <item.icon />}
      <Typography margin="0 0 0 0.4rem" color="inherit">
        {item.title}
      </Typography>
    </StyledNavItem>
  );
};
